import { fabric } from 'fabric';

import { primaryFamily } from './fabric-setup';
import type { FontPair } from './types';

/**
 * The font library.
 *
 * Curated, not exhaustive: every face here earns its place on a tile, rug or
 * furniture brand's feed — editorial serifs for headlines, neutral sans for
 * body copy and specs, a few scripts for signatures, and Arabic faces for RTL
 * vendors. `axes` is the exact Google Fonts css2 axis spec; one stylesheet per
 * family so a wrong spec can only break that family.
 *
 * The stylesheet only declares @font-face rules — the browser downloads a file
 * when something asks for it. Canvas text never "asks", so every use goes
 * through loadFont(), which calls document.fonts.load for the exact
 * weight/style, then re-measures the text.
 */
export type FontCategory = 'serif' | 'sans' | 'display' | 'script' | 'arabic';

export interface FontDef {
    family: string;
    category: FontCategory;
    axes?: string;
    weights: number[];
    italic?: boolean;
}

export const FONT_LIBRARY: FontDef[] = [
    // Serif display
    { family: 'Playfair Display', category: 'serif', axes: 'ital,wght@0,400;0,500;0,600;0,700;0,800;1,400;1,500', weights: [400, 500, 600, 700, 800], italic: true },
    { family: 'Cormorant Garamond', category: 'serif', axes: 'ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,500', weights: [300, 400, 500, 600, 700], italic: true },
    { family: 'DM Serif Display', category: 'serif', axes: 'ital@0;1', weights: [400], italic: true },
    { family: 'Fraunces', category: 'serif', axes: 'ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400', weights: [300, 400, 500, 600, 700], italic: true },
    { family: 'Instrument Serif', category: 'serif', axes: 'ital@0;1', weights: [400], italic: true },
    { family: 'Libre Caslon Text', category: 'serif', axes: 'ital,wght@0,400;0,700;1,400', weights: [400, 700], italic: true },
    { family: 'Bodoni Moda', category: 'serif', axes: 'ital,wght@0,400;0,500;0,600;0,700;1,400', weights: [400, 500, 600, 700], italic: true },
    { family: 'Libre Baskerville', category: 'serif', axes: 'ital,wght@0,400;0,700;1,400', weights: [400, 700], italic: true },
    { family: 'Italiana', category: 'serif', weights: [400] },
    // Sans
    { family: 'Inter', category: 'sans', axes: 'wght@300;400;500;600;700;800', weights: [300, 400, 500, 600, 700, 800] },
    { family: 'Geist', category: 'sans', axes: 'wght@300;400;500;600;700', weights: [300, 400, 500, 600, 700] },
    { family: 'Manrope', category: 'sans', axes: 'wght@300;400;500;600;700;800', weights: [300, 400, 500, 600, 700, 800] },
    { family: 'DM Sans', category: 'sans', axes: 'ital,wght@0,300;0,400;0,500;0,600;0,700;1,400', weights: [300, 400, 500, 600, 700], italic: true },
    { family: 'Montserrat', category: 'sans', axes: 'ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400', weights: [300, 400, 500, 600, 700, 800], italic: true },
    { family: 'Poppins', category: 'sans', axes: 'ital,wght@0,300;0,400;0,500;0,600;0,700;1,400', weights: [300, 400, 500, 600, 700], italic: true },
    { family: 'Archivo', category: 'sans', axes: 'ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400', weights: [300, 400, 500, 600, 700, 800], italic: true },
    { family: 'Space Grotesk', category: 'sans', axes: 'wght@300;400;500;600;700', weights: [300, 400, 500, 600, 700] },
    // Display
    { family: 'Syne', category: 'display', axes: 'wght@400;500;600;700;800', weights: [400, 500, 600, 700, 800] },
    { family: 'Bebas Neue', category: 'display', weights: [400] },
    { family: 'Oswald', category: 'display', axes: 'wght@300;400;500;600;700', weights: [300, 400, 500, 600, 700] },
    // Script
    { family: 'Allura', category: 'script', weights: [400] },
    { family: 'Great Vibes', category: 'script', weights: [400] },
    { family: 'Pinyon Script', category: 'script', weights: [400] },
    { family: 'Dancing Script', category: 'script', axes: 'wght@400;500;600;700', weights: [400, 500, 600, 700] },
    // Arabic
    { family: 'Tajawal', category: 'arabic', axes: 'wght@300;400;500;700;800', weights: [300, 400, 500, 700, 800] },
    { family: 'Cairo', category: 'arabic', axes: 'wght@300;400;500;600;700;800', weights: [300, 400, 500, 600, 700, 800] },
];

export const FONT_CATEGORIES: FontCategory[] = ['serif', 'sans', 'display', 'script', 'arabic'];

/** Pairings used by text combos and templates. Heading face first. */
export const FONT_PAIRS: FontPair[] = [
    { id: 'editorial', heading: 'Playfair Display', headingWeight: 400, body: 'Inter' },
    { id: 'gallery', heading: 'Cormorant Garamond', headingWeight: 500, body: 'Manrope' },
    { id: 'atelier', heading: 'Fraunces', headingWeight: 300, body: 'DM Sans' },
    { id: 'maison', heading: 'Bodoni Moda', headingWeight: 400, body: 'Montserrat' },
    { id: 'studio', heading: 'Instrument Serif', headingWeight: 400, body: 'Geist' },
    { id: 'modern', heading: 'Syne', headingWeight: 600, body: 'DM Sans' },
    { id: 'bold', heading: 'Bebas Neue', headingWeight: 400, body: 'Inter', headingUpper: true },
    { id: 'grotesk', heading: 'Space Grotesk', headingWeight: 500, body: 'Space Grotesk' },
];

export const DEFAULT_PAIR = FONT_PAIRS[0];

/**
 * Map a brand kit's typeface name onto a family we can load. Kits are read
 * off websites and name faces loosely ("Bodoni", "Playfair") — the closest
 * library family wins, otherwise the name is kept and tried as-is.
 */
export function resolveFontFamily(name: string): string {
    const clean = primaryFamily(name).trim();
    const lower = clean.toLowerCase();
    const exact = FONT_LIBRARY.find((f) => f.family.toLowerCase() === lower);
    if (exact) return exact.family;
    const prefix = FONT_LIBRARY.find((f) => f.family.toLowerCase().startsWith(lower) || lower.startsWith(f.family.toLowerCase()));
    return prefix ? prefix.family : clean;
}

const findFont = (family: string) => FONT_LIBRARY.find((f) => f.family.toLowerCase() === family.toLowerCase());

const injected = new Map<string, Promise<void>>();

/** Inject one family's stylesheet (idempotent). Unknown families are tried as plain Google Fonts. */
function injectStylesheet(family: string): Promise<void> {
    const key = family.toLowerCase();
    const existing = injected.get(key);
    if (existing) return existing;
    const def = findFont(family);
    const name = family.trim().replace(/ /g, '+');
    const spec = def?.axes ? `${name}:${def.axes}` : name;
    const href = `https://fonts.googleapis.com/css2?family=${spec}&display=swap`;
    const p = new Promise<void>((resolve) => {
        if (typeof document === 'undefined') { resolve(); return; }
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.dataset.csFont = family;
        link.onload = () => resolve();
        // A family Google does not have is not fatal — the stack falls back.
        link.onerror = () => resolve();
        document.head.appendChild(link);
    });
    injected.set(key, p);
    return p;
}

const LOCAL_FAMILIES = new Set(['serif', 'sans-serif', 'monospace', 'helvetica', 'arial', 'georgia', 'times new roman', 'cursive']);

/**
 * Make one face usable on canvas: stylesheet in, then the specific file pulled
 * by document.fonts.load. Resolves even on failure — a missing font must never
 * block the editor.
 */
export async function loadFont(familyOrStack: string, weight: number | string = 400, italic = false): Promise<void> {
    const family = primaryFamily(familyOrStack);
    if (!family || LOCAL_FAMILIES.has(family.toLowerCase())) return;
    // No Font Loading API (a test DOM): nothing could be loaded or awaited.
    if (typeof document === 'undefined' || !(document as any).fonts) return;
    await Promise.race([injectStylesheet(family), new Promise((r) => setTimeout(r, 5000))]);
    try {
        const fonts = (document as any).fonts;
        if (fonts?.load) {
            await Promise.race([
                fonts.load(`${italic ? 'italic ' : ''}${weight} 48px "${family}"`),
                new Promise((r) => setTimeout(r, 4000)),
            ]);
        }
    } catch { /* fall back silently */ }
}

/** Every face a set of serialised or live objects uses. */
export function fontsInObjects(objects: any[]): Array<{ family: string; weight: number | string; italic: boolean }> {
    const out: Array<{ family: string; weight: number | string; italic: boolean }> = [];
    const walk = (o: any) => {
        if (!o) return;
        if (typeof o.text === 'string' && o.fontFamily) {
            out.push({ family: primaryFamily(o.fontFamily), weight: o.fontWeight || 400, italic: o.fontStyle === 'italic' });
        }
        const kids = o.objects || (typeof o.getObjects === 'function' ? o.getObjects() : null);
        if (Array.isArray(kids)) kids.forEach(walk);
    };
    objects.forEach(walk);
    return out;
}

/** Load every face a scene needs. */
export async function loadFontsFor(objects: any[]): Promise<void> {
    const seen = new Set<string>();
    const jobs: Promise<void>[] = [];
    for (const f of fontsInObjects(objects)) {
        const k = `${f.family}|${f.weight}|${f.italic}`;
        if (seen.has(k)) continue;
        seen.add(k);
        jobs.push(loadFont(f.family, f.weight, f.italic));
    }
    await Promise.all(jobs);
}

/**
 * After a face arrives, Fabric's cached glyph widths for it are wrong (they
 * were measured in the fallback). Clear them and re-measure every text layer.
 */
export function remeasureText(canvas: fabric.StaticCanvas | null, family?: string): void {
    if (!canvas) return;
    try {
        (fabric.util as any).clearFabricFontCache?.(family);
    } catch { /* older fabric */ }
    const walk = (o: any) => {
        if (typeof o.text === 'string' && typeof o.initDimensions === 'function') {
            if (!family || primaryFamily(o.fontFamily) === family) {
                o.initDimensions();
                o.setCoords?.();
                o.dirty = true;
            }
        }
        if (typeof o.getObjects === 'function') o.getObjects().forEach(walk);
    };
    canvas.getObjects().forEach(walk);
    canvas.requestRenderAll();
}

/** Inject the stylesheets for the whole library so the picker can preview faces. */
export function preloadLibraryStylesheets(): void {
    FONT_LIBRARY.forEach((f) => { void injectStylesheet(f.family); });
}

export function fontWeightsFor(family: string): number[] {
    return findFont(primaryFamily(family))?.weights || [300, 400, 500, 600, 700];
}

export function fontHasItalic(family: string): boolean {
    const def = findFont(primaryFamily(family));
    return def ? !!def.italic : true;
}
